import {
  Box,
  FieldLabel,
  Flex,
  Form,
  MonetaryInputField,
  NumericFormatField,
  type OperationType,
  SelectField,
  SubmitButton,
  Text,
  TextInputField,
} from '@/components';
import { useProject } from '@/features/project/_hooks/project';
import { isBefore2025 } from '@/features/project/_utils/project';
import { FundBusinessForInvestmentUpsertFormSchema } from '@/features/project/projects/detail/fund-bussiness/_model/fund-business';
import { getInitialPerformancesDataForInvestment } from '@/features/project/projects/detail/fund-bussiness/_utils/fund-business';
import { useCurrentFiscalYear } from '@/features/search/_hooks/get-current-fiscal-year';
import { useForm, useId } from '@/hooks';
import { useLocaledAmount } from '@/providers';
import { zodResolver } from '@hookform/resolvers/zod';
import { groupBy } from 'lodash-es';
import type { FC } from 'react';
import { useFieldArray } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import type { z } from 'zod';
import {
  BusinessFormLabelModifier,
  businessForm,
} from '../../../_models/project';

type FormValues = z.infer<typeof FundBusinessForInvestmentUpsertFormSchema>;

type Props = {
  type: OperationType;
  initialValues?: Partial<FormValues>;
  onSubmit: (values: FormValues) => void;
};

export const FundBusinessForInvestmentForm: FC<Props> = ({
  type,
  initialValues = {
    performances: getInitialPerformancesDataForInvestment(),
  },
  onSubmit,
}) => {
  const { t } = useTranslation();
  const { unit } = useLocaledAmount();
  const { id: projectId } = useId();
  const project = useProject(projectId);
  const {
    control,
    handleSubmit,
    formState: { isSubmitting, errors, isDirty, isSubmitSuccessful },
  } = useForm<FormValues>({
    defaultValues: initialValues,
    resolver: zodResolver(FundBusinessForInvestmentUpsertFormSchema),
  });
  const { fields } = useFieldArray({ control, name: 'performances' });
  const currentFiscalYear = useCurrentFiscalYear();

  const isEdit = type === 'edit';
  const fiscalYear =
    project.data?.fiscalYear ??
    currentFiscalYear.data ??
    currentFiscalYear.basicFiscalYear;

  const groupByYearFields = groupBy(
    fields.map((value, index) => ({
      ...value,
      index,
      year: fiscalYear - Math.floor(index / 2),
    })),
    'year',
  );
  const fieldList = Object.entries(groupByYearFields).map(([year, data]) => ({
    year: Number(year),
    data,
  }));

  return (
    <Form
      leavePageAlertCondition={{
        isDirty,
        isSubmitting,
        isSubmitSuccessful,
      }}
      layout="vertical"
      onFinish={handleSubmit(onSubmit)}
    >
      <SelectField
        control={control}
        required
        disabled={isEdit}
        label={t('fund-business-investment-type')}
        errors={errors}
        name="businessForm"
        options={businessForm.options.map((value) => ({
          value,
          label: BusinessFormLabelModifier[value],
        }))}
      />
      <TextInputField
        control={control}
        label={t('fund-business-title')}
        errors={errors}
        name="title"
        autocompleteKey="fundBusiness.title"
      />
      {fieldList.map((field) => {
        const isEstimatedYear = field.year >= fiscalYear;

        return (
          <Form.Item
            key={field.year}
            label={
              <FieldLabel
                text={
                  isEstimatedYear
                    ? t('fund-business-estimate-amount-with-year', {
                        year: field.year,
                      })
                    : t('fund-business-amount-with-year', {
                        year: field.year,
                      })
                }
              />
            }
          >
            <Flex vertical gap={8}>
              {field.data.map((performance) => {
                const index = performance.index;
                return (
                  <Flex key={index} align="center">
                    <Box flex={1}>
                      <Text text={performance.title} weight="regular" />
                    </Box>
                    <Box flex={2} paddingLeft={8} paddingRight={16}>
                      <NumericFormatField
                        name={`performances.${index}.yearAmountInfos.0.amountInfo.count`}
                        control={control}
                        errors={errors}
                        addonAfter={t('number-unit')}
                        marginBottom={0}
                        allowNegative={false}
                        disabled={
                          !isBefore2025({ fiscalYear }) &&
                          field.year === fiscalYear - 2
                        }
                      />
                    </Box>
                    <Box flex={5}>
                      <MonetaryInputField
                        name={`performances.${index}.yearAmountInfos.0.amountInfo.amount`}
                        control={control}
                        errors={errors}
                        unit={unit}
                        marginBottom={0}
                        disabled={
                          !isBefore2025({ fiscalYear }) &&
                          field.year === fiscalYear - 2
                        }
                      />
                    </Box>
                  </Flex>
                );
              })}
            </Flex>
          </Form.Item>
        );
      })}
      <SubmitButton loading={isSubmitting} />
    </Form>
  );
};
